/* nm-cart.js — cart drawer extras (markup: snippets/cart-drawer.liquid):
   - "Panier réservé pendant 05:00" countdown (starts when the drawer is first opened, persisted in sessionStorage)
   - free gifts: when the main product is in the cart, add the gift variants (line property _cadeau) ; remove them when it leaves
   - shipping-protection toggle (ON by default, choice remembered in sessionStorage)
   - upsell "AJOUTER" button (one-time or selling plan)
   Every change re-renders the drawer with the Section Rendering API (cart-drawer + cart-icon-bubble), like Dawn's cart.js. */
(() => {
  if (window.NmCart) return;

  const SECTIONS = 'cart-drawer,cart-icon-bubble';
  const TIMER_KEY = 'nm_cart_timer_end';
  const PROTECT_KEY = 'nm_protect';
  const TIMER_SECONDS = 300;
  const FREEBIE_MIN_MS = 1800;

  const store = {
    get: (k) => { try { return sessionStorage.getItem(k); } catch (e) { return null; } },
    set: (k, v) => { try { sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } },
  };
  const routes = () => window.routes || { cart_url: '/cart', cart_add_url: '/cart/add', cart_update_url: '/cart/update' };
  const drawerEl = () => document.querySelector('cart-drawer');
  const onCartPage = () => !!document.getElementById('main-cart-items');
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const request = (url, body) =>
    fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    }).then((r) => r.json().then((d) => { if (!r.ok || d.status) throw d; return d; }));
  const cfg = () => {
    const el = document.getElementById('NmCartCfg');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  };
  const cartSig = (cart) => cart.items.map((i) => `${i.key}:${i.quantity}|`).join('');

  let observer = null;
  let busy = false;
  let again = false;
  const attempts = {};

  /* ---------- rendering ---------- */
  function applySections(sections) {
    if (!sections) return;
    const d = drawerEl();
    if (d && sections['cart-drawer']) {
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const src = doc.querySelector('.drawer__inner');
      const dst = d.querySelector('.drawer__inner');
      if (src && dst) dst.innerHTML = src.innerHTML;
      const srcDrawer = doc.querySelector('cart-drawer');
      if (srcDrawer) d.classList.toggle('is-empty', srcDrawer.classList.contains('is-empty'));
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && sections['cart-icon-bubble']) {
      const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
      const s = doc.querySelector('.shopify-section');
      if (s) bubble.innerHTML = s.innerHTML;
    }
    if (observer) observer.takeRecords();
    tickTimer();
  }

  async function refresh() {
    const r = await fetch(`${window.location.pathname}?sections=${SECTIONS}`);
    applySections(await r.json());
  }

  function showFreebie(on) {
    document.querySelectorAll('[data-nm-freebie]').forEach((el) => { el.hidden = !on; });
  }

  /* ---------- gifts + protection ---------- */
  async function sync() {
    const c = cfg();
    if (!c) return;
    if (busy) { again = true; return; }
    busy = true;
    try {
      const cart = await request(`${routes().cart_url}.js`);
      const isGift = (i) => i.properties && i.properties._cadeau;
      const hasMain = cart.items.some((i) => i.handle === c.main && !isGift(i));
      const gifts = cart.items.filter(isGift);
      const prot = cart.items.filter((i) => i.variant_id === c.protect && !isGift(i));
      const real = cart.items.filter((i) => !isGift(i) && i.variant_id !== c.protect);
      const wantProt = !!c.protect && store.get(PROTECT_KEY) !== '0';
      const updates = {};
      const addGifts = [];
      const addProt = [];

      if (hasMain) {
        (c.gifts || []).forEach((id) => {
          if (!gifts.some((g) => g.variant_id === id)) addGifts.push({ id, quantity: 1, properties: { _cadeau: '1' } });
        });
        gifts.forEach((g) => { if (g.quantity !== 1) updates[g.key] = 1; });
      } else {
        gifts.forEach((g) => { updates[g.key] = 0; });
      }
      if (real.length && wantProt) {
        if (!prot.length) addProt.push({ id: c.protect, quantity: 1 });
        prot.forEach((p, n) => { if (n > 0) updates[p.key] = 0; else if (p.quantity !== 1) updates[p.key] = 1; });
      } else {
        prot.forEach((p) => { updates[p.key] = 0; });
      }

      const todo = Object.keys(updates).length + addGifts.length + addProt.length;
      const wrap = document.querySelector('cart-drawer .nm-cd');
      const stale = cartSig(cart) !== c.sig || (wrap && wrap.dataset.sig !== c.sig);
      const key = JSON.stringify([updates, addGifts.map((a) => a.id), addProt.map((a) => a.id)]);
      if (todo) attempts[key] = (attempts[key] || 0) + 1;
      if (!todo || attempts[key] > 2) {
        if (stale) await refresh();
        return;
      }

      const t0 = Date.now();
      if (addGifts.length) showFreebie(true);
      const base = { sections: SECTIONS, sections_url: window.location.pathname };
      let sections = null;
      let changed = false;
      if (Object.keys(updates).length) {
        try { sections = (await request(`${routes().cart_update_url}.js`, { updates, ...base })).sections; changed = true; } catch (e) { console.warn('[nm-cart] update', e); }
      }
      for (const items of [addGifts, addProt]) {
        if (!items.length) continue;
        try { sections = (await request(`${routes().cart_add_url}.js`, { items, ...base })).sections || sections; changed = true; } catch (e) { console.warn('[nm-cart] add', e); }
      }
      if (addGifts.length) await wait(Math.max(0, FREEBIE_MIN_MS - (Date.now() - t0)));
      if (changed && onCartPage()) { window.location.reload(); return; }
      if (sections) applySections(sections);
      else { showFreebie(false); if (changed || stale) await refresh(); }
    } catch (e) {
      console.warn('[nm-cart]', e);
    } finally {
      busy = false;
      if (again) { again = false; sync(); }
    }
  }

  /* ---------- upsell ---------- */
  async function upsellAdd(btn) {
    const card = btn.closest('.nm-cd__up-card');
    const sel = card && card.querySelector('[data-nm-upsell-plan]');
    const item = { id: Number(btn.dataset.variantId), quantity: 1 };
    if (sel && sel.value) item.selling_plan = Number(sel.value);
    btn.setAttribute('aria-busy', 'true');
    try {
      const r = await request(`${routes().cart_add_url}.js`, { items: [item], sections: SECTIONS, sections_url: window.location.pathname });
      if (onCartPage()) { window.location.reload(); return; }
      applySections(r.sections);
    } catch (e) {
      console.warn('[nm-cart] upsell', e);
      btn.removeAttribute('aria-busy');
    }
  }

  /* ---------- timer ---------- */
  function tickTimer() {
    const els = document.querySelectorAll('[data-nm-timer]');
    if (!els.length) return;
    const d = drawerEl();
    let end = parseInt(store.get(TIMER_KEY), 10) || 0;
    if (!end && d && d.classList.contains('active')) { end = Date.now() + TIMER_SECONDS * 1000; store.set(TIMER_KEY, String(end)); }
    if (end && end <= Date.now()) { end = Date.now() + TIMER_SECONDS * 1000; store.set(TIMER_KEY, String(end)); }
    const left = end ? Math.max(0, Math.round((end - Date.now()) / 1000)) : TIMER_SECONDS;
    const txt = `${String(Math.floor(left / 60)).padStart(2, '0')}:${String(left % 60).padStart(2, '0')}`;
    els.forEach((el) => { if (el.firstChild && el.firstChild.nodeValue !== txt) el.firstChild.nodeValue = txt; });
  }

  /* ---------- events ---------- */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-nm-protect-line] cart-remove-button')) store.set(PROTECT_KEY, '0');
    const t = e.target.closest('[data-nm-protect]');
    if (t) {
      e.preventDefault();
      const on = t.getAttribute('aria-checked') !== 'true';
      t.setAttribute('aria-checked', String(on));
      store.set(PROTECT_KEY, on ? '1' : '0');
      Object.keys(attempts).forEach((k) => delete attempts[k]);
      sync();
      return;
    }
    const b = e.target.closest('[data-nm-upsell-add]');
    if (b) { e.preventDefault(); upsellAdd(b); }
  });

  document.addEventListener('change', (e) => {
    const s = e.target.closest('[data-nm-upsell-plan]');
    if (!s) return;
    const o = s.options[s.selectedIndex];
    const card = s.closest('.nm-cd__up-card');
    const p = card && card.querySelector('[data-nm-up-price]');
    const c = card && card.querySelector('[data-nm-up-compare]');
    if (p) p.textContent = o.dataset.price || '';
    if (c) c.textContent = o.dataset.compare || '';
    if (observer) observer.takeRecords();
  });

  function init() {
    const d = drawerEl();
    if (d) {
      let t;
      observer = new MutationObserver(() => { clearTimeout(t); t = setTimeout(sync, 80); });
      observer.observe(d, { childList: true, subtree: true });
    }
    tickTimer();
    setInterval(tickTimer, 1000);
    const c = cfg();
    if (c && c.count > 0) sync();
    // /cart page: Dawn's cart.js does not re-render the drawer, so listen to its cart updates
    if (onCartPage() && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => setTimeout(sync, 80));
    }
  }

  window.NmCart = { sync, refresh, open: () => drawerEl() && drawerEl().open() };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
